import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";

const CircularProgressBar = ({
  // Legacy
  value = null,
  maxValue = 100,

  // Array mode
  data = null, // [{ name, value, color?, colorDepth? }]

  // Common
  variant = "halfdonut", // "halfdonut" | "
  // +"
  size = 200,
  strokeWidth = 20,
  duration = 0.9,
  delay = 0,
  fillColor = "primary",
  showPercentage = true,
  showLabels = true,
  textColor = "text-gray-700",
  backgroundLinecap = "butt",
  progressLinecap = "round",
  className = "",
}) => {
  const [animatedPercentages, setAnimatedPercentages] = useState({});
  const [animatedTotal, setAnimatedTotal] = useState(0);

  const isArrayMode = data && Array.isArray(data) && data.length > 0;
  const actualProgressLinecap = isArrayMode ? "butt" : progressLinecap;

  const getColorValue = (colorName = "primary", depth = 500) =>
    `var(--color-${colorName}-${depth})`;

  const processedData = useMemo(() => {
    if (isArrayMode) {
      const total = data.reduce((s, it) => s + (it?.value || 0), 0);
      let cumulative = 0;
      return data.map((it) => {
        const raw = it?.value || 0;
        const percentage = total > 0 ? (raw / total) * 100 : 0;
        const startPercentage = total > 0 ? (cumulative / total) * 100 : 0;
        cumulative += raw;
        const endPercentage = total > 0 ? (cumulative / total) * 100 : 0;
        return {
          ...it,
          percentage,
          startPercentage,
          endPercentage,
          color: it?.color || "primary",
          colorDepth: it?.colorDepth || 500,
          displayValue: raw,
        };
      });
    }

    if (value !== null) {
      const percentage = Math.min((value / maxValue) * 100, 100);
      return [
        {
          name: "Progress",
          value,
          percentage,
          startPercentage: 0,
          endPercentage: percentage,
          color: "primary",
          colorDepth: 500,
          displayValue: value,
        },
      ];
    }
    return [];
  }, [data, value, maxValue, isArrayMode]);

  const totalValue = processedData.reduce(
    (s, it) => s + (it.displayValue || 0),
    0
  );
  const totalPercentage = processedData.reduce(
    (s, it) => s + (it.percentage || 0),
    0
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      const start = Date.now();
      const animDur = duration * 1000;
      const step = () => {
        const elapsed = Date.now() - start;
        const p = Math.min(elapsed / animDur, 1);
        const easeOut = 1 - Math.pow(1 - p, 3);

        const newAnimated = {};
        processedData.forEach((it, i) => {
          newAnimated[i] = Math.round(it.percentage * easeOut);
        });
        setAnimatedPercentages(newAnimated);
        setAnimatedTotal(Math.round(totalPercentage * easeOut));

        if (p < 1) requestAnimationFrame(step);
      };
      step();
    }, delay * 1000);

    return () => clearTimeout(timer);
  }, [processedData, duration, delay, totalPercentage]);

  const center = size / 2;
  const radius = (size - strokeWidth) / 2;

  const makeArcPath = (startPercent, endPercent) => {
    const startAngle = (startPercent / 100) * Math.PI * 2 - Math.PI / 2;
    const endAngle = (endPercent / 100) * Math.PI * 2 - Math.PI / 2;

    let delta = endAngle - startAngle;
    if (delta < 0) delta += Math.PI * 2;

    if (delta >= Math.PI * 2 - 1e-6) return { fullCircle: true };

    const sx = center + radius * Math.cos(startAngle);
    const sy = center + radius * Math.sin(startAngle);
    const ex = center + radius * Math.cos(endAngle);
    const ey = center + radius * Math.sin(endAngle);

    const largeArcFlag = delta > Math.PI ? 1 : 0;
    const d = `M ${sx} ${sy} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${ex} ${ey}`;
    return { d, delta };
  };

  // HALF DONUT
  if (variant === "halfdonut") {
    const halfRadius = radius;
    const halfCenterY = center;
    // Determine fill color
    const mainFillColor = fillColor ? fillColor.replace(/^bg-/, "") : "primary";
    // lighter background color
    const backgroundColor = `var(--color-${mainFillColor}-100, #e5e7eb)`;

    return (
      <div
        className={`relative inline-flex flex-col items-center justify-center ${className}`}
      >
        <svg
          width={size}
          height={size / 2 + strokeWidth}
          viewBox={`0 0 ${size} ${size / 2 + strokeWidth}`}
          className="overflow-visible"
        >
          <path
            d={`M ${
              strokeWidth / 2
            } ${center} A ${halfRadius} ${halfRadius} 0 0 1 ${
              size - strokeWidth / 2
            } ${center}`}
            fill="none"
            stroke={backgroundColor}
            strokeWidth={strokeWidth}
            strokeLinecap={backgroundLinecap}
          />

          {processedData.map((seg, idx) => {
            if (!seg || seg.percentage === 0) return null;
            const startAngle = (seg.startPercentage / 100) * Math.PI;
            const endAngle = (seg.endPercentage / 100) * Math.PI;

            const sx = center + halfRadius * Math.cos(Math.PI - startAngle);
            const sy = center - halfRadius * Math.sin(Math.PI - startAngle);
            const ex = center + halfRadius * Math.cos(Math.PI - endAngle);
            const ey = center - halfRadius * Math.sin(Math.PI - endAngle);

            const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;
            const d = `M ${sx} ${sy} A ${halfRadius} ${halfRadius} 0 ${largeArcFlag} 1 ${ex} ${ey}`;

            const color = getColorValue(seg.color, seg.colorDepth);
            return (
              <motion.path
                key={idx}
                d={d}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
                strokeLinecap={actualProgressLinecap}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{
                  duration,
                  delay: delay + idx * 0.06,
                  ease: "easeOut",
                }}
              />
            );
          })}
        </svg>

        {/* CENTER TEXT */}
        {showPercentage && (
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center pointer-events-none ${textColor} font-semibold text-center`}
          >
            {isArrayMode
              ? `${Math.round(animatedTotal)}%`
              : `${animatedPercentages[0] || 0}%`}
          </div>
        )}

        {showLabels && isArrayMode && (
          <div className="mt-4 space-y-2">
            {processedData.map((seg, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: getColorValue(seg.color, seg.colorDepth),
                  }}
                />
                <span className={textColor}>
                  {seg.name}: {Math.round(seg.percentage)}% ({seg.displayValue})
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // FULL DONUT
  if (variant === "fulldonut") {
    const mainFillColor = fillColor ? fillColor.replace(/^bg-/, "") : "primary";

    // Background color should be lighter version (depth 100)
    const backgroundColor = `var(--color-${mainFillColor}-100, #e5e7eb)`;

    return (
      <div className={`flex flex-col items-center ${className}`}>
        <div
          className="relative flex items-center justify-center"
          style={{ width: size, height: size }}
        >
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={backgroundColor}
              strokeWidth={strokeWidth}
              strokeLinecap={backgroundLinecap}
            />

            {processedData.map((seg, idx) => {
              if (!seg || seg.percentage === 0) return null;
              const { d, fullCircle } = makeArcPath(
                seg.startPercentage,
                seg.endPercentage
              );
              const color = getColorValue(seg.color, seg.colorDepth);

              if (fullCircle) {
                return (
                  <motion.circle
                    key={idx}
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeLinecap={actualProgressLinecap}
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{
                      duration,
                      delay: delay + idx * 0.06,
                      ease: "easeOut",
                    }}
                  />
                );
              }

              return (
                <motion.path
                  key={idx}
                  d={d}
                  fill="none"
                  stroke={color}
                  strokeWidth={strokeWidth}
                  strokeLinecap={actualProgressLinecap}
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{
                    duration,
                    delay: delay + idx * 0.06,
                    ease: "easeOut",
                  }}
                />
              );
            })}
          </svg>

          {/* CENTER TEXT */}
          {showPercentage && (
            <div
              className={`absolute inset-0 flex flex-col items-center justify-center pointer-events-none ${textColor} font-semibold text-center`}
            >
              {isArrayMode ? (
                <>
                  <div className="text-xl font-bold">{totalValue}</div>
                  <div className="text-sm opacity-70">Total</div>
                </>
              ) : (
                <div className="text-2xl font-bold">
                  {animatedPercentages[0] || 0}%
                </div>
              )}
            </div>
          )}
        </div>

        {/* LABELS */}
        {showLabels && isArrayMode && (
          <div className="mt-4 grid grid-cols-2 gap-2 text-sm max-w-xs">
            {processedData.map((seg, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{
                    backgroundColor: getColorValue(seg.color, seg.colorDepth),
                  }}
                />
                <span className="truncate">
                  {seg.name}: {seg.displayValue}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default CircularProgressBar;
